import { type MousePosition } from "@react-hook/mouse-position";
import { type TreeNode } from "@/routes/sessions/DeviceContext";
import { type Dimensions } from "@/utils/types";

const pointInBounds = (
  boundsX: number,
  boundsY: number,
  boundsWidth: number,
  boundsHeight: number,
  x: number,
  y: number
) => {
  return (
    x >= boundsX &&
    x <= boundsX + boundsWidth &&
    y >= boundsY &&
    y <= boundsY + boundsHeight
  );
};

const getHoveredElement = ({
  dimensions,
  elements,
  mouse,
  usingGptDriverDeviceProvider,
}: {
  dimensions: Dimensions;
  elements: TreeNode[];
  mouse: MousePosition;
  usingGptDriverDeviceProvider: boolean;
}): TreeNode | null => {
  if (elements.length === 0) {
    return null;
  }
  const hoveredList = elements.filter((element) => {
    if (element.bounds == null) return false;

    const text = element.text ?? element.attributes?.text;
    const resourceId =
      element.resourceId ?? element.attributes?.["resource-id"];
    const accessibilityText =
      (element as any).accessibilityText ??
      element.attributes?.accessibilityText ??
      element.attributes?.["accessibility-text"] ??
      element.attributes?.["content-desc"];

    if (!text && !resourceId && !accessibilityText) return false;
    const {
      x: boundsX,
      y: boundsY,
      width: boundsWidth,
      height: boundsHeight,
    } = element.bounds;

    const { x: mouseX, y: mouseY, elementWidth, elementHeight } = mouse;

    if (
      mouseX === null ||
      mouseY === null ||
      elementWidth === null ||
      elementHeight === null
    ) {
      return false;
    }

    return pointInBounds(
      boundsX / dimensions.width,
      boundsY / dimensions.height,
      boundsWidth / dimensions.width,
      boundsHeight / dimensions.height,
      mouseX / elementWidth,
      mouseY / elementHeight
    );
  });

  if (hoveredList.length === 0) return null;

  // Sort by actual path values (lexicographic comparison)
  return hoveredList.sort((a, b) => {
    if (a.bounds == null && b.bounds == null) return 0;
    if (a.bounds == null) return 1;
    if (b.bounds == null) return -1;

    // TODO should use drawing order, we currently use the element size as a workaround for the appetize sending outdated data from getUI method
    if (!usingGptDriverDeviceProvider) {
      return (
        a.bounds.width * a.bounds.height - b.bounds.width * b.bounds.height
      );
    }

    const aPath = a.drawingOrderPath ?? a.indexPath ?? [];
    const bPath = b.drawingOrderPath ?? b.indexPath ?? [];

    // Lexicographic comparison of path arrays (descending - higher values first)
    for (let i = 0; i < Math.min(aPath.length, bPath.length); i++) {
      const aVal = parseInt(aPath[i]);
      const bVal = parseInt(bPath[i]);
      if (aVal !== bVal) {
        return bVal - aVal; // Descending order - higher path values win
      }
    }

    // If no differences found in common path segments, longer path comes first (child before parent)
    if (aPath.length !== bPath.length) return bPath.length - aPath.length;

    // Final tiebreaker: Smaller elements first (more specific interactive controls)
    return a.bounds.width * a.bounds.height - b.bounds.width * b.bounds.height;
  })[0];
};

const toPercent = (n: number, total: number) =>
  `${Math.round((100 * n) / total)}%`;

const getMouseHint = (mouse: MousePosition): string | null => {
  if (
    typeof mouse.x !== "number" ||
    typeof mouse.y !== "number" ||
    typeof mouse.elementWidth !== "number" ||
    typeof mouse.elementHeight !== "number"
  ) {
    return null;
  }
  const x = toPercent(mouse.x, mouse.elementWidth);
  const y = toPercent(mouse.y, mouse.elementHeight);
  return `${x}, ${y}`;
};

export { getHoveredElement, getMouseHint, toPercent };
